<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Text } from '@sitecore-jss/sitecore-jss-vue';
import type { ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import componentFactory from '@/temp/componentFactory';
import type { SidebarItemFields, SidebarProps } from '@/types/components/Sidebar';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

type SidebarSource = ScItem<SidebarItemFields> & {
  uid?: string;
  rendering?: ComponentRendering;
};

type DynamicComponentItem = ComponentRendering & {
  id?: string;
  template?: string;
  templateName?: string;
};

interface NormalizedSidebarItem extends SidebarSource {
  itemId: string;
  titleField: SidebarItemFields['title'];
  titleValue: string;
  anchorId: string;
  components: DynamicComponentItem[];
}

const props = defineProps<SidebarProps>();

const activeItemId = ref<string>();
const scrollSyncLocked = ref(false);
const headerOffset = ref(0);
const rootEl = ref<HTMLElement | null>(null);
const sidebarEl = ref<HTMLElement | null>(null);

const fields = computed(() => props.fields || {});
const sourceItems = computed<SidebarSource[]>(() => fields.value.items ?? []);
const rootStyle = computed(() => ({
  ...(utils.overall?.baseComStyle?.(fields.value) || {}),
  '--sidebar-header-offset': `${headerOffset.value}px`,
}));
const rootClass = computed(() => utils.overall.baseComClass(fields.value, 'c-sidebar'));
const activeItem = computed(() => items.value.find((item) => item.itemId === activeItemId.value));

function getFieldValue(field?: string | { value?: string }) {
  if (typeof field === 'string') return field;
  return field?.value ?? '';
}

function stripHtml(value?: string) {
  return `${value ?? ''}`
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function slugify(value?: string) {
  return stripHtml(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getComponentName(componentData?: DynamicComponentItem) {
  return `${
    componentData?.componentName ||
    componentData?.templateName ||
    componentData?.template ||
    'FeaturedResourceCard'
  }`.replace(/\s+/g, '');
}

function resolveDynamicComponent(componentData?: DynamicComponentItem) {
  const componentName = getComponentName(componentData);
  return componentFactory(componentName) || componentName;
}

function getItemKey(item: SidebarSource, index: number) {
  return (
    getFieldValue(item?.fields?.key) ||
    stripHtml(getFieldValue(getTitleField(item))) ||
    item?.id ||
    item?.uid ||
    `sidebar-item-${index + 1}`
  );
}

function getTitleField(item: SidebarSource): SidebarItemFields['title'] {
  return item?.fields?.title ?? { value: item?.displayName ?? item?.name ?? '' };
}

const items = computed<NormalizedSidebarItem[]>(() =>
  sourceItems.value.map((item, index) => {
    const titleField = getTitleField(item);
    const titleValue = stripHtml(getFieldValue(titleField));
    const key = getItemKey(item, index);

    return {
      ...item,
      itemId: `sidebar-item-${slugify(key) || index + 1}`,
      titleField,
      titleValue,
      anchorId: `sidebar-${slugify(key) || index + 1}`,
      components: (item?.fields?.components ?? []).map((componentData) => ({
        ...componentData,
        componentName: getComponentName(componentData),
      })),
    };
  })
);

watch(
  items,
  (list) => {
    if (!list.length) {
      activeItemId.value = undefined;
      return;
    }

    if (!list.some((item) => item.itemId === activeItemId.value)) {
      activeItemId.value = list[0].itemId;
    }
  },
  { immediate: true }
);

function updateHeaderOffset() {
  if (typeof window === 'undefined') return;

  const headerEl =
    window.document.querySelector('#header-content .c-gen-nav') ??
    window.document.querySelector('.c-gen-nav');
  const headerTopEl = headerEl?.querySelector('.c-gen-nav__top');
  const topRect = headerTopEl?.getBoundingClientRect?.();
  headerOffset.value = Math.round(
    topRect?.bottom ?? headerEl?.getBoundingClientRect?.().bottom ?? 0
  );
}

function scrollToItem(item: NormalizedSidebarItem, behavior: ScrollBehavior = 'smooth') {
  const anchorEl = document.getElementById(item.anchorId);
  if (!anchorEl) return;

  const top = window.scrollY + anchorEl.getBoundingClientRect().top - headerOffset.value - 24;
  window.scrollTo({ top, behavior });

  if (window.history?.replaceState) {
    window.history.replaceState(null, '', `#${item.anchorId}`);
  }
}

function syncActiveItemByScroll() {
  if (!items.value.length || scrollSyncLocked.value) return;

  const detectLine = headerOffset.value + 80;
  let nextActive = items.value[0];

  for (const item of items.value) {
    const anchorEl = document.getElementById(item.anchorId);
    if (!anchorEl) continue;

    if (anchorEl.getBoundingClientRect().top <= detectLine) {
      nextActive = item;
    } else {
      break;
    }
  }

  if (nextActive.itemId !== activeItemId.value) {
    activeItemId.value = nextActive.itemId;
  }
}

function unlockScrollSync() {
  scrollSyncLocked.value = false;
}

function onItemClick(item: NormalizedSidebarItem) {
  activeItemId.value = item.itemId;
  scrollSyncLocked.value = true;
  scrollToItem(item);
  window.setTimeout(unlockScrollSync, 900);
}

function isActiveItem(item: NormalizedSidebarItem) {
  return Boolean(activeItemId.value) && activeItemId.value === item.itemId;
}

function getDynamicComponentProps(componentData: DynamicComponentItem) {
  return {
    fields: componentData?.fields ?? {},
    rendering: componentData,
    page: props.page,
  };
}

function getIconSrc(item: NormalizedSidebarItem) {
  return item.fields?.iconImage?.value?.src || '';
}

function getIconAlt(item: NormalizedSidebarItem) {
  return item.fields?.iconImage?.value?.alt || item.titleValue || '';
}

function applyHashItem() {
  if (typeof window === 'undefined') return;

  const hash = decodeURIComponent(window.location.hash.slice(1));
  if (!hash) return;

  const target = items.value.find((item) => item.anchorId === hash);
  if (target) {
    activeItemId.value = target.itemId;
    nextTick(() => scrollToItem(target, 'auto'));
  }
}

let scrollHandler: (() => void) | undefined;
let resizeHandler: (() => void) | undefined;

onMounted(() => {
  nextTick(() => {
    updateHeaderOffset();
    applyHashItem();
    syncActiveItemByScroll();
  });

  scrollHandler = () => {
    updateHeaderOffset();
    syncActiveItemByScroll();
  };
  resizeHandler = () => {
    updateHeaderOffset();
    syncActiveItemByScroll();
  };

  window.addEventListener('scroll', scrollHandler, { passive: true });
  window.addEventListener('resize', resizeHandler);
});

onBeforeUnmount(() => {
  if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
  if (resizeHandler) window.removeEventListener('resize', resizeHandler);
});
</script>

<template>
  <section ref="rootEl" :class="rootClass" :style="rootStyle">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-sidebar__layout">
        <aside ref="sidebarEl" class="c-sidebar__nav" aria-label="Resource sections">
          <button
            v-for="item in items"
            :key="item.itemId"
            :class="['c-sidebar__nav-item', { active: isActiveItem(item) }]"
            type="button"
            :aria-current="isActiveItem(item) ? 'true' : undefined"
            :aria-controls="item.anchorId"
            @click="onItemClick(item)"
          >
            <span class="c-sidebar__icon-wrap">
              <img
                v-if="getIconSrc(item)"
                class="c-sidebar__icon-image"
                :src="getIconSrc(item)"
                :alt="getIconAlt(item)"
              />
              <span
                v-else
                :class="[
                  'c-sidebar__icon',
                  `c-sidebar__icon--${item.fields?.icon?.value || 'tube'}`,
                ]"
              />
            </span>
            <Text class="c-sidebar__nav-title" :field="item.titleField" />
            <span class="c-sidebar__arrow" aria-hidden="true"></span>
          </button>
        </aside>

        <div class="c-sidebar__content">
          <section
            v-for="item in items"
            :id="item.anchorId"
            :key="item.itemId"
            class="c-sidebar__panel"
            :aria-label="item.titleValue"
          >
            <component
              :is="resolveDynamicComponent(componentData)"
              v-for="(componentData, componentIndex) in item.components"
              v-bind="getDynamicComponentProps(componentData)"
              :key="componentData?.uid ?? componentData?.id ?? `${item.itemId}-${componentIndex}`"
            />
          </section>
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-sidebar {
  display: block;
  width: 100%;
  background-color: var(--back-color, $white);
  color: var(--color, #111827);

  &__layout {
    display: grid;
    gap: 28px;
  }

  &__nav {
    display: grid;
    gap: 16px;
  }

  &__nav-item {
    display: grid;
    grid-template-columns: 76px minmax(0, 1fr) 24px;
    gap: 22px;
    align-items: center;
    width: 100%;
    min-height: 140px;
    border: 1px solid #dde3ea;
    border-radius: 24px;
    background: $white;
    padding: 28px 24px 28px 32px;
    color: #4b5565;
    text-align: left;
    box-shadow: none;
    cursor: pointer;
    transition:
      background 0.2s ease,
      border-color 0.2s ease,
      box-shadow 0.2s ease,
      color 0.2s ease;

    &.active {
      border-color: #6f9a78;
      background: #6f9a78;
      color: $white;
      box-shadow: 0 12px 24px rgba(17, 24, 39, 0.18);

      .c-sidebar__icon-wrap {
        background: rgba(255, 255, 255, 0.18);
        color: $white;
      }

      .c-sidebar__arrow {
        display: block;
      }
    }
  }

  &__icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 76px;
    border-radius: 20px;
    background: #f4f5f8;
    color: #4b5565;
  }

  &__nav-title {
    color: currentColor;
    font-size: 26px;
    font-weight: 800;
    line-height: 1.22;
  }

  &__arrow {
    position: relative;
    display: none;
    width: 18px;
    height: 18px;
    border-top: 4px solid currentColor;
    border-right: 4px solid currentColor;
    transform: rotate(45deg);
  }

  &__content {
    display: grid;
    gap: 36px;
    min-width: 0;
  }

  &__panel {
    scroll-margin-top: 120px;

    .c-featured-resource-card {
      padding: 0;
    }
  }

  &__icon {
    position: relative;
    display: block;
    width: 34px;
    height: 34px;
  }

  &__icon-image {
    width: 34px;
    height: 34px;
    object-fit: contain;
    filter: brightness(0) saturate(100%) invert(31%) sepia(14%) saturate(664%) hue-rotate(177deg)
      brightness(93%) contrast(87%);
  }

  &__nav-item.active &__icon-image {
    filter: brightness(0) invert(1);
  }

  &__icon::before,
  &__icon::after {
    position: absolute;
    content: '';
  }

  &__icon--tube::before {
    inset: 4px 9px;
    border: 4px solid currentColor;
    border-top: 0;
    border-radius: 0 0 4px 4px;
  }

  &__icon--tube::after {
    top: 2px;
    left: 7px;
    width: 20px;
    height: 4px;
    border-radius: 4px;
    background: currentColor;
    box-shadow: 0 10px 0 currentColor;
  }

  &__icon--team::before {
    top: 5px;
    left: 4px;
    width: 10px;
    height: 10px;
    border: 3px solid currentColor;
    border-radius: 50%;
    box-shadow: 16px 0 0 -3px currentColor;
  }

  &__icon--team::after {
    right: 2px;
    bottom: 5px;
    left: 2px;
    height: 12px;
    border: 3px solid currentColor;
    border-bottom: 0;
    border-radius: 14px 14px 0 0;
  }

  &__icon--network::before {
    top: 4px;
    right: 3px;
    width: 8px;
    height: 8px;
    border: 3px solid currentColor;
    border-radius: 50%;
    box-shadow:
      -22px 11px 0 -3px currentColor,
      -3px 22px 0 -3px currentColor;
  }

  &__icon--network::after {
    top: 11px;
    left: 9px;
    width: 20px;
    height: 15px;
    border-top: 3px solid currentColor;
    border-bottom: 3px solid currentColor;
    transform: rotate(-30deg);
  }

  &__icon--document::before {
    inset: 4px 7px 3px 8px;
    border: 3px solid currentColor;
    border-radius: 4px;
  }

  &__icon--document::after {
    top: 11px;
    left: 14px;
    width: 10px;
    height: 3px;
    background: currentColor;
    box-shadow:
      0 7px 0 currentColor,
      0 14px 0 currentColor;
  }

  &__icon--award::before {
    top: 3px;
    left: 8px;
    width: 18px;
    height: 18px;
    border: 3px solid currentColor;
    border-radius: 50%;
  }

  &__icon--award::after {
    top: 20px;
    left: 10px;
    width: 14px;
    height: 12px;
    border-right: 4px solid currentColor;
    border-left: 4px solid currentColor;
    transform: skewX(-12deg);
  }

  &__icon--video::before {
    top: 8px;
    left: 2px;
    width: 22px;
    height: 16px;
    border: 3px solid currentColor;
    border-radius: 5px;
  }

  &__icon--video::after {
    top: 11px;
    right: 1px;
    width: 12px;
    height: 12px;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-left: 10px solid currentColor;
  }

  @include tablet {
    &__layout {
      grid-template-columns: minmax(210px, 280px) minmax(0, 1fr);
      align-items: start;
      gap: 32px;
    }

    &__nav {
      position: sticky;
      top: calc(var(--sidebar-header-offset, 0px) + 24px);
      gap: 14px;
      max-height: calc(100vh - var(--sidebar-header-offset, 0px) - 48px);
      overflow: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__nav-item {
      grid-template-columns: 42px minmax(0, 1fr) 12px;
      gap: 14px;
      min-height: 70px;
      border-radius: 10px;
      padding: 12px 14px;
    }

    &__icon-wrap {
      width: 42px;
      height: 42px;
      border-radius: 10px;
    }

    &__icon {
      width: 22px;
      height: 22px;
      transform: scale(0.68);
      transform-origin: center;
    }

    &__icon-image {
      width: 22px;
      height: 22px;
    }

    &__nav-title {
      font-size: 13px;
      line-height: 1.25;
    }

    &__arrow {
      width: 9px;
      height: 9px;
      border-width: 2px;
    }

    &__content {
      gap: 60px;
    }
  }
}
</style>
